import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Edit2, Save, X, Trash2, AlertTriangle, Copy, CheckCircle2, UserMinus, UserX, GraduationCap, Link2, ArchiveRestore, ChevronDown, ChevronUp, Users, BookOpen, UserCheck } from 'lucide-react';
import { toast } from 'sonner';
import { PageLayout } from '../../shared/PageLayout';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { ConfirmDialog } from '../../../components/shared/ConfirmDialog';
import { AddLearnerSheet } from '../components/AddLearnerSheet';
import { ClassroomTeachersTab } from '../components/ClassroomTeachersTab';
import { CoTeacherWelcomeModal } from '../components/CoTeacherWelcomeModal';
import { TransferClassroomModal } from '../components/TransferClassroomModal';
import { useAppStore } from '../../../app/hooks/useAppStore';
import { DjayNarrator } from '../../../mascot';
import { getLearnerClassroomFlags } from '../utils/studentUtils';
import { type Classroom, type TeacherAccount, type ClassroomValidationWarning, generateClassroomId, validateNewClassroom, findDuplicateClassroomName, isLearnerAccount, findClassroomByCoTeacherCode, CO_TEACHER_CODE_LENGTH, getClassroomRole, MAX_CO_TEACHERS_PER_CLASSROOM } from '../../../accounts';
import { StudentData } from './StudentDetailPanel';
interface ManagementPageProps {
  activeAccount: TeacherAccount;
  classrooms?: Classroom[];
  selectedClassroomId?: string | null;
  students: StudentData[];
}
export function ManagementPage({
  activeAccount,
  classrooms = [],
  selectedClassroomId = null,
  students
}: ManagementPageProps) {
  const {
    accounts,
    addClassroom,
    updateClassroom,
    removeClassroom,
    removeLearnerFromClassroom,
    unlinkTeacherFromLearner,
    restoreArchivedLearner,
    joinClassroomAsCoTeacher,
    regenerateJoinCode
  } = useAppStore();
  const [isJoiningAsCoTeacher, setIsJoiningAsCoTeacher] = useState(false);
  const [coTeacherCode, setCoTeacherCode] = useState('');
  const [coTeacherJoinError, setCoTeacherJoinError] = useState<string | null>(null);
  const [coTeacherWelcomeFor, setCoTeacherWelcomeFor] = useState<Classroom | null>(null);
  const handleJoinAsCoTeacher = () => {
    setCoTeacherJoinError(null);
    const code = coTeacherCode.trim().toUpperCase();
    if (!code) {
      setCoTeacherJoinError('Vul eerst een code in.');
      return;
    }
    // Zoekt UITSLUITEND op de docentcode, nooit op de klascode.
    //
    // Hier stond `findClassroomByJoinCode`, waardoor de code die dertig
    // leerlingen kennen ook docentrechten gaf (SHORT_CODE_SECURITY.md B-1).
    const target = findClassroomByCoTeacherCode(accounts, code);
    if (!target) {
      // Een leerlingcode is aan de lengte te herkennen. Dat apart benoemen
      // verraadt niets — de invoerder heeft die code zelf — en voorkomt dat een
      // collega blijft proberen met de verkeerde code in de hand.
      if (code.length === 6) {
        setCoTeacherJoinError('Dit lijkt de klascode voor leerlingen. Vraag de eigenaar om de docentcode; die is 8 tekens lang.');
        return;
      }
      setCoTeacherJoinError('Geen klas gevonden met deze docentcode.');
      return;
    }
    const role = getClassroomRole(accounts, target.classroom.id, activeAccount.id);
    if (role === 'owner') {
      setCoTeacherJoinError('Je bent al de eigenaar van deze klas.');
      return;
    }
    if (role === 'co-teacher') {
      setCoTeacherJoinError('Je bent al co-docent in deze klas.');
      return;
    }
    const currentCoTeachers = target.classroom.coTeacherIds ?? [];
    if (currentCoTeachers.length >= MAX_CO_TEACHERS_PER_CLASSROOM) {
      setCoTeacherJoinError(`Deze klas heeft al ${MAX_CO_TEACHERS_PER_CLASSROOM} co-docenten. Vraag de eigenaar om iemand te verwijderen.`);
      return;
    }
    joinClassroomAsCoTeacher(activeAccount.id, target.classroom.id);
    setCoTeacherWelcomeFor(target.classroom);
    setIsJoiningAsCoTeacher(false);
    setCoTeacherCode('');
  };
  const [isEditingClass, setIsEditingClass] = useState<string | null>(null);
  const [editClassName, setEditClassName] = useState('');
  const [editGroupLevel, setEditGroupLevel] = useState(6);
  const [isAddingClass, setIsAddingClass] = useState(false);
  const [newClassName, setNewClassName] = useState('');
  const [newGroupLevel, setNewGroupLevel] = useState(6);
  const [classWarnings, setClassWarnings] = useState<ClassroomValidationWarning[]>([]);
  const [confirmDuplicate, setConfirmDuplicate] = useState(false);
  const [deleteClassroomId, setDeleteClassroomId] = useState<string | null>(null);
  const [transferClassroomId, setTransferClassroomId] = useState<string | null>(null);
  const [regenerateCodeClassroomId, setRegenerateCodeClassroomId] = useState<string | null>(null);
  const [showAddStudent, setShowAddStudent] = useState(false);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [newClassroomId, setNewClassroomId] = useState<string | null>(null);
  const [pendingRemoveStudent, setPendingRemoveStudent] = useState<{
    studentId: string;
    studentName: string;
    classroomId: string;
    classroomName: string;
  } | null>(null);
  const [pendingUnlinkStudent, setPendingUnlinkStudent] = useState<{
    studentId: string;
    studentName: string;
  } | null>(null);
  const [showArchive, setShowArchive] = useState(false);
  const [activeTab, setActiveTab] = useState<'leerlingen' | 'klassen' | 'docenten' | 'archief'>('leerlingen');
  const handleSaveClass = (classroomId: string) => {
    if (editClassName.trim()) {
      const duplicates = findDuplicateClassroomName(accounts, editClassName.trim(), classroomId);
      if (duplicates.length > 0) {
        const dup = duplicates[0];
        const isSameTeacher = dup.classroom.teacherId === activeAccount.id;
        setClassWarnings([{
          type: 'duplicate_name',
          message: isSameTeacher ? `Je hebt al een klas met de naam "${dup.classroom.name}".` : `"${dup.classroom.name}" bestaat al bij ${dup.teacherName}.`,
          details: {
            classroomName: dup.classroom.name,
            classroomId: dup.classroom.id,
            teacherName: dup.teacherName,
            teacherId: dup.classroom.teacherId
          }
        }]);
        return;
      }
      updateClassroom(activeAccount.id, classroomId, {
        name: editClassName.trim(),
        groupLevel: editGroupLevel
      });
      setIsEditingClass(null);
      setClassWarnings([]);
    }
  };
  const handleAddClassroom = () => {
    if (!newClassName.trim()) return;
    const warnings = validateNewClassroom(accounts, newClassName.trim(), activeAccount.id);
    const hasSameTeacherDuplicate = warnings.some(w => w.details.teacherId === activeAccount.id);
    if (hasSameTeacherDuplicate) {
      setClassWarnings(warnings);
      return;
    }
    if (warnings.length > 0 && !confirmDuplicate) {
      setClassWarnings(warnings);
      return;
    }
    const newClassroom: Classroom = {
      id: generateClassroomId(),
      name: newClassName.trim(),
      groupLevel: newGroupLevel,
      learnerIds: [],
      createdAt: Date.now(),
      teacherId: activeAccount.id,
      joinCode: '' // Auto-generated by addClassroom()
    };
    addClassroom(activeAccount.id, newClassroom);
    setIsAddingClass(false);
    setNewClassName('');
    setNewGroupLevel(6);
    setClassWarnings([]);
    setConfirmDuplicate(false);
    setNewClassroomId(newClassroom.id);
    setTimeout(() => setNewClassroomId(null), 3000);
  };
  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };
  const activeClassroom = selectedClassroomId ? classrooms.find(c => c.id === selectedClassroomId) : null;
  const classStudents = (activeClassroom ? students.filter(s => activeClassroom.learnerIds.includes(s.id)) : students).sort((a, b) => a.name.localeCompare(b.name));
  const classroomToDelete = deleteClassroomId ? classrooms.find(c => c.id === deleteClassroomId) : null;
  const archivedLinks = accounts.relationships.archivedLearnerLinks?.[activeAccount.id] || [];
  const recentArchivedLinks = archivedLinks.filter(link => Date.now() - link.archivedAt < 30 * 24 * 60 * 60 * 1000).sort((a, b) => b.archivedAt - a.archivedAt);
  return <PageLayout title="Klas Beheer" description="Beheer je klassen en leerlingen" data-id="element-10199" data-mp="9i7js0g40d2y">
      
      <div className="max-w-5xl mx-auto space-y-6 pb-12" data-id="element-10200" data-mp="3qqm5stp29yqw">
        {/* Segmented Tabs */}
        <div className="sticky top-0 z-10 bg-slate-50/80 backdrop-blur-md p-1.5 rounded-xl border border-slate-200 flex items-center gap-1 overflow-x-auto custom-scrollbar shadow-sm" data-id="element-10201" data-mp="3qsyqvfq48p86">
          <button onClick={() => setActiveTab('leerlingen')} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all whitespace-nowrap ${activeTab === 'leerlingen' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'}`} data-id="element-10202" data-mp="1ox4wllla8pu1">
            
            <Users className="w-4 h-4" data-id="element-10203" data-mp="3o667ynca7gzd" />
            Leerlingen
          </button>
          <button onClick={() => setActiveTab('klassen')} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all whitespace-nowrap ${activeTab === 'klassen' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'}`} data-id="element-10204" data-mp="3o896xwvewkg8">
            
            <BookOpen className="w-4 h-4" data-id="element-10205" data-mp="3t7lt0feglnhr" />
            Klassen
          </button>
          <button onClick={() => setActiveTab('docenten')} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all whitespace-nowrap ${activeTab === 'docenten' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'}`} data-id="element-10206" data-mp="ayv10edjznbl">
            
            <UserCheck className="w-4 h-4" data-id="element-10207" data-mp="27u0zl9j7qhp6" />
            Docenten
          </button>
          {recentArchivedLinks.length > 0 && <button onClick={() => setActiveTab('archief')} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all whitespace-nowrap ${activeTab === 'archief' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'}`} data-id="element-10208" data-mp="jb3m3xft8ypo">
            
              <ArchiveRestore className="w-4 h-4" data-id="element-10209" data-mp="170epznsgj981" />
              Archief ({recentArchivedLinks.length})
            </button>}
        </div>

        {/* Students Management Section */}
        {activeTab === 'leerlingen' && <Card className="border-slate-200 shadow-sm bg-white overflow-hidden" data-id="element-10210" data-mp="xa3ad3zevblb">
            <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-100" data-id="element-10211" data-mp="t9jc1x33q1xl">
              <div data-id="element-10212" data-mp="1prpbfpfeytsj">
                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-10213" data-mp="1prz63qbelp4k">
                  Leerlingen Beheer
                </h3>
                <p className="text-xs text-slate-400 mt-0.5" data-id="element-10214" data-mp="1kymkzq5xo0gk">
                  {activeClassroom ? `${activeClassroom.name} (${classStudents.length} leerlingen)` : `Alle gekoppelde leerlingen (${classStudents.length})`}
                </p>
              </div>
              <Button onClick={() => setShowAddStudent(true)} variant="primary" size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl text-sm font-bold transition-all flex items-center gap-2" data-id="element-10215" data-mp="3cn3yctq57dy8">
              
                <Plus className="w-4 h-4" data-id="element-10216" data-mp="3rl685lnrovnx" />
                Leerling Toevoegen
              </Button>
            </div>

            <div className="p-4" data-id="element-10217" data-mp="5ysmagunj9i">
              {classStudents.length === 0 ? <div className="text-center py-8 bg-slate-50 rounded-xl border border-slate-200 border-dashed" data-id="element-10218" data-mp="8l7yw9zf32a">
                  <div className="mb-3 flex justify-center" data-id="element-10219" data-mp="976qirfgei32">
                    <DjayNarrator size="md" emotion="surprised" data-id="element-10220" data-mp="98q8qnko737h" />
                  </div>
                  <p className="text-slate-500 font-medium" data-id="element-10221" data-mp="e71ikyaytdpp">
                    {activeClassroom ? `Nog geen leerlingen in ${activeClassroom.name}.` : 'Je hebt nog geen leerlingen gekoppeld.'}
                  </p>
                  <p className="text-xs text-slate-400 mt-1" data-id="element-10222" data-mp="25c4pdol03mm5">
                    {activeClassroom ? 'Voeg leerlingen toe via de knop hierboven.' : 'Selecteer een klas in de zijbalk of voeg leerlingen toe.'}
                  </p>
                </div> : <div className="divide-y divide-slate-100 border border-slate-200 rounded-xl overflow-hidden bg-white" data-id="element-10223" data-mp="2szm8kvq79cdi">
                  <div className="flex items-center justify-between p-4 bg-slate-50 text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-10224" data-mp="2oz2czwl9ygth">
                    <div data-id="element-10225" data-mp="32xadgehis09e">Leerling</div>
                    <div data-id="element-10226" data-mp="2xz8oxe1br8qj">Acties</div>
                  </div>
                  {classStudents.map((student, i) => {
              const initial = student.name.charAt(0).toUpperCase();
              const flags = getLearnerClassroomFlags(accounts, student.id, activeClassroom || null);
              const learnerAccount = accounts.accountsById[student.id];
              const learnerGrade = learnerAccount && isLearnerAccount(learnerAccount) ? learnerAccount.grade : undefined;
              return <div key={student.id} className={`flex items-center justify-between px-4 py-2.5 hover:bg-slate-50 transition-colors ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}`} data-id="element-10227" data-mp="35ihvkzig4tbw" data-mpi={i}>
                    
                        <div className="flex items-center gap-3 flex-wrap" data-id="element-10228" data-mp="2wi37xti0f258">
                          <div className="w-8 h-8 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold text-sm shadow-sm" data-id="element-10229" data-mp="2wjwe77opnkcp">
                            {initial}
                          </div>
                          <div className="font-bold text-sm text-slate-900" data-id="element-10230" data-mp="2rigvhma09pxh">
                            {student.name}
                          </div>
                          {flags.gradeMismatch && learnerGrade && <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-bold rounded-md" title={`Werkt op groep-${learnerGrade}-niveau (deze klas is groep ${activeClassroom?.groupLevel}).`} data-id="element-10231" data-mp="prdhqo4tjv8p">
                        
                              <GraduationCap className="w-3 h-3" data-id="element-10232" data-mp="2htoodj9s9cu2" />
                              Gr {learnerGrade}
                            </span>}
                          {flags.gradeUnknown && <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-600 text-xs font-bold rounded-md" title="Deze leerling heeft nog geen groep ingesteld." data-id="element-10233" data-mp="27txpl5e6wbo6">
                        
                              <GraduationCap className="w-3 h-3" data-id="element-10234" data-mp="1y3mij9z7lmd2" />
                              Geen groep
                            </span>}
                          {(flags.multiClassroom || flags.multiTeacher) && <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-sky-100 text-sky-700 text-xs font-bold rounded-md" title={flags.otherEnrollments.length > 0 ? `Ook in: ${flags.otherEnrollments.map(e => e.classroomName + ' (' + e.teacherName + ')').join(', ')}` : 'Gekoppeld aan meer dan een leerkracht'} data-id="element-10235" data-mp="1p62j86kwq053">
                        
                              <Link2 className="w-3 h-3" data-id="element-10236" data-mp="1a0okszii9mz9" />
                              {flags.otherEnrollments.length > 0 ? '+' + flags.otherEnrollments.length + (flags.otherEnrollments.length === 1 ? ' klas' : ' klassen') : 'Meerdere koppelingen'}
                            </span>}
                          {!activeClassroom && !flags.gradeMismatch && !flags.gradeUnknown && learnerGrade && <span className="px-1.5 py-0.5 bg-slate-100 text-slate-500 text-xs font-bold rounded" data-id="element-10237" data-mp="m9sobp33u3lo">
                                Gr {learnerGrade}
                              </span>}
                        </div>
                        <div className="flex items-center gap-2" data-id="element-10238" data-mp="1111hu9a4o943">
                          {activeClassroom ? <>
                              <button onClick={() => {
                      setPendingRemoveStudent({
                        studentId: student.id,
                        studentName: student.name,
                        classroomId: activeClassroom.id,
                        classroomName: activeClassroom.name
                      });
                    }} className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-bold text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors group" title="Verwijder uit klas" data-id="element-10239" data-mp="3k2rkwfs77r4">
                          
                                <UserX className="w-4 h-4" data-id="element-10240" data-mp="3dpjdj8cguapa" />
                                <span className="hidden sm:inline opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-10241" data-mp="3drw2w6kjzncd">
                                  Verwijder uit klas
                                </span>
                              </button>
                              {getClassroomRole(accounts, activeClassroom.id, activeAccount.id) === 'owner' && <>
                                  <div className="w-px h-4 bg-slate-200 mx-1" data-id="element-10242" data-mp="12wggfb8skzue"></div>
                                  <button onClick={() => {
                        setPendingUnlinkStudent({
                          studentId: student.id,
                          studentName: student.name
                        });
                      }} className="p-1.5 text-slate-300 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors" title="Ontkoppel volledig van jouw account" data-id="element-10243" data-mp="xxvhrg96uo82">
                            
                                    <UserMinus className="w-4 h-4" data-id="element-10244" data-mp="10lfnov2k0lvj" />
                                  </button>
                                </>}
                            </> : <button onClick={() => {
                    setPendingUnlinkStudent({
                      studentId: student.id,
                      studentName: student.name
                    });
                  }} className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-bold text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors group" title="Ontkoppel volledig van jouw account" data-id="element-10245" data-mp="1t8a3vfsc8z37">
                        
                              <UserMinus className="w-4 h-4" data-id="element-10246" data-mp="2c0cmgwojipg3" />
                              <span className="hidden sm:inline opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-10247" data-mp="1n9f4fxduy5o2">
                                Ontkoppel
                              </span>
                            </button>}
                        </div>
                      </div>;
            })}
                </div>}
            </div>
          </Card>}

        {/* Classes Section */}
        {activeTab === 'klassen' && <Card className="border-slate-200 shadow-sm bg-white overflow-hidden" data-id="element-10248" data-mp="265yfis8823n9">
            <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-100" data-id="element-10249" data-mp="2186ftc1c5d8i">
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-10250" data-mp="1w6phnoa8k4vm">
                Mijn Klassen
              </h3>
              <div className="flex items-center gap-2" data-id="element-10251" data-mp="kt57k4cuzf78">
                <Button onClick={() => setIsAddingClass(true)} variant="primary" size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl text-sm font-bold transition-all flex items-center gap-2" data-id="element-10252" data-mp="kuom9qiv9i6w">
                
                  <Plus className="w-4 h-4" data-id="element-10253" data-mp="2wggz6wybov4y" />
                  Nieuwe Klas
                </Button>
              </div>
            </div>

            <div className="p-4 space-y-4" data-id="element-10254" data-mp="31f1tkfqw8klh">
              {isAddingClass && <div className="p-5 bg-indigo-50/50 border border-indigo-100 rounded-xl" data-id="element-10255" data-mp="3671xskdzxznv">
                  <h4 className="text-sm font-bold text-slate-900 mb-3" data-id="element-10256" data-mp="3b5wpycxut0kn">
                    Nieuwe klas toevoegen
                  </h4>
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3" data-id="element-10257" data-mp="3f5x4dxkydbrp">
                    <div className="flex-1 w-full" data-id="element-10258" data-mp="3k7cn3iznr66x">
                      <Input variant="admin" label="Klasnaam" value={newClassName} onChange={e => setNewClassName(e.target.value)} onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddClassroom();
                  }
                }} placeholder="Bijv. Groep 7B" autoFocus data-id="element-10259" data-mp="3p5e6y64wr9jg" />
                  
                    </div>
                    <div className="w-full sm:w-32" data-id="element-10260" data-mp="7p57r2t4mpt5">
                      <Select variant="admin" label="Niveau" value={newGroupLevel} onChange={e => setNewGroupLevel(parseInt(e.target.value))} options={[{
                  value: '6',
                  label: 'Groep 6'
                }, {
                  value: '7',
                  label: 'Groep 7'
                }, {
                  value: '8',
                  label: 'Groep 8'
                }]} data-id="element-10261" data-mp="7px4wi2dgvlj" />
                  
                    </div>
                    <div className="flex items-center gap-2 self-end sm:self-auto mt-2 sm:mt-5" data-id="element-10262" data-mp="vezjti5mgacc">
                      <Button onClick={handleAddClassroom} className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl text-sm font-bold transition-all flex items-center gap-2" data-id="element-10263" data-mp="zg1inxvmmkds">
                    
                        <Save className="w-4 h-4" data-id="element-10264" data-mp="316wj530s37qu" />
                        Aanmaken
                      </Button>
                      <Button onClick={() => {
                  setIsAddingClass(false);
                  setClassWarnings([]);
                  setConfirmDuplicate(false);
                }} variant="secondary" data-id="element-10265" data-mp="2tp6yzpvwoufp">
                    
                        Annuleren
                      </Button>
                    </div>
                  </div>
                  {classWarnings.length > 0 && <div className="mt-3 space-y-2" data-id="element-10266" data-mp="3cnj9beyk0acv">
                      {classWarnings.map((w, i) => <div key={i} className={`flex items-start gap-2 p-3 rounded-lg text-sm ${w.details.teacherId === activeAccount.id ? 'bg-red-50 border border-red-200 text-red-800' : 'bg-amber-50 border border-amber-200 text-amber-800'}`} data-id="element-10267" data-mp="37o6f295cs109" data-mpi={i}>
                  
                          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-10268" data-mp="2ymypcfgwk8hs" />
                          <div data-id="element-10269" data-mp="1s8r3nkq1oxy3">
                            <p className="font-medium" data-id="element-10270" data-mp="1s4tlp2f7ecfs">{w.message}</p>
                            {w.details.teacherId !== activeAccount.id && !confirmDuplicate && <button onClick={() => setConfirmDuplicate(true)} className="mt-1 text-xs font-bold underline hover:no-underline" data-id="element-10271" data-mp="1ooz38uw6ceiy">
                      
                                  Ja, toch aanmaken
                                </button>}
                          </div>
                        </div>)}
                    </div>}
                </div>}

              {classrooms.length === 0 && !isAddingClass ? <div className="text-center py-10 bg-slate-50 rounded-xl border border-slate-200 border-dashed" data-id="element-10272" data-mp="15xppg9fdf3lk">
                  <div className="mb-4 flex justify-center" data-id="element-10273" data-mp="1ar22rnelnnz0">
                    <DjayNarrator size="md" emotion="thinking" data-id="element-10274" data-mp="w136jq6lumm6" />
                  </div>
                  <p className="text-slate-500 mb-4 font-medium" data-id="element-10275" data-mp="10zehk48mz35z">
                    Je hebt nog geen klassen aangemaakt.
                  </p>
                  <Button onClick={() => setIsAddingClass(true)} variant="outline" className="border-indigo-200 text-indigo-600 hover:bg-indigo-50 hover:border-indigo-300" data-id="element-10276" data-mp="n17u7i1bclkk">
                
                    Maak je eerste klas aan
                  </Button>
                </div> : <div className="grid grid-cols-1 gap-3" data-id="element-10277" data-mp="3ems9le0ip0q">
                  {classrooms.map((classroom: Classroom) => <motion.div key={classroom.id} initial={newClassroomId === classroom.id ? {
              backgroundColor: '#f0fdf4',
              borderColor: '#86efac'
            } : {}} animate={{
              backgroundColor: '#ffffff',
              borderColor: '#e2e8f0'
            }} transition={{
              duration: 2,
              delay: 1
            }} className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-white border border-slate-200 rounded-xl hover:border-indigo-200 transition-colors shadow-sm" data-id="element-10278" data-mp="hee0t6lrfd7c">
                
                      {isEditingClass === classroom.id ? <div className="flex-1 flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full" data-id="element-10279" data-mp="1a1s0hcguemv5">
                          <div className="flex-1 w-full" data-id="element-10280" data-mp="154rsgw0fna1e">
                            <Input variant="admin" value={editClassName} onChange={e => setEditClassName(e.target.value)} onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSaveClass(classroom.id);
                    }
                  }} placeholder="Klasnaam" autoFocus data-id="element-10281" data-mp="2zjx871yn4qeq" />
                    
                          </div>
                          <div className="w-full sm:w-32" data-id="element-10282" data-mp="2glcjtho4vz4s">
                            <Select variant="admin" value={editGroupLevel} onChange={e => setEditGroupLevel(parseInt(e.target.value))} options={[{
                    value: '6',
                    label: 'Groep 6'
                  }, {
                    value: '7',
                    label: 'Groep 7'
                  }, {
                    value: '8',
                    label: 'Groep 8'
                  }]} data-id="element-10283" data-mp="2gmw2tg16coa9" />
                    
                          </div>
                          <div className="flex items-center gap-2 self-end sm:self-auto mt-2 sm:mt-0" data-id="element-10284" data-mp="9ss92z3l324p">
                            <Button onClick={() => handleSaveClass(classroom.id)} className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl text-sm font-bold transition-all flex items-center gap-2" data-id="element-10285" data-mp="jxs81cig9do4">
                      
                              <Save className="w-4 h-4" data-id="element-10286" data-mp="66vjbwrn9gmc" />
                              Opslaan
                            </Button>
                            <Button onClick={() => {
                    setIsEditingClass(null);
                    setClassWarnings([]);
                  }} variant="secondary" size="sm" data-id="element-10287" data-mp="18txj42u3376">
                      
                              <X className="w-4 h-4" data-id="element-10288" data-mp="3nfs9s9xewmta" />
                            </Button>
                          </div>
                        </div> : <>
                          <div className="flex items-center gap-4 mb-3 sm:mb-0" data-id="element-10289" data-mp="1lndr38n1nebt">
                            <div className="w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-600 font-black text-lg border border-indigo-100" data-id="element-10290" data-mp="1hm2attq285ga">
                              {classroom.groupLevel}
                            </div>
                            <div data-id="element-10291" data-mp="3fx2flpn68yek">
                              <h4 className="font-bold text-slate-900 text-base" data-id="element-10292" data-mp="3fxca9qj5vtql">
                                {classroom.name}
                              </h4>
                              <div className="flex items-center gap-3 text-sm text-slate-500 font-medium mt-0.5" data-id="element-10293" data-mp="3667refqd7oqh">
                                <span data-id="element-10294" data-mp="3tt66b8yu6oma">
                                  {classroom.learnerIds.length} leerlingen
                                </span>
                                <span className="w-1 h-1 rounded-full bg-slate-300" data-id="element-10295" data-mp="3kvdo9x1qd2v7" />
                                <span data-id="element-10296" data-mp="3pt5snqjkahga">
                                  {(classroom.coTeacherIds?.length || 0) + 1}{' '}
                                  docenten
                                </span>
                                <span className="w-1 h-1 rounded-full bg-slate-300" data-id="element-10297" data-mp="2ef1qhx2tln05" />
                                <div className="flex items-center gap-1.5" data-id="element-10298" data-mp="25x82c90kwg37">
                                  <span data-id="element-10299" data-mp="25zch5m00mzi9">Code:</span>
                                  <span className="font-mono font-bold text-slate-700 bg-slate-100 px-1.5 py-0.5 rounded" data-id="element-10300" data-mp="21uuzbsd7uwa4">
                                    {classroom.joinCode}
                                  </span>
                                  {getClassroomRole(accounts, classroom.id, activeAccount.id) === 'owner' && <>
                                      <button onClick={() => handleCopyCode(classroom.joinCode)} className="text-slate-400 hover:text-indigo-600 transition-colors" title="Kopieer code" data-id="element-10301" data-mp="1n4xhdu5vr2td">
                              
                                        {copiedCode === classroom.joinCode ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" data-id="element-10302" data-mp="1s5tmgb1ywv0d" /> : <Copy className="w-3.5 h-3.5" data-id="element-10303" data-mp="1s4a5ei8hmfxj" />}
                                      </button>
                                      {/*
                                       "Nieuwe code" leest als toevoegen,
                                       terwijl deze knop de oude ONGELDIG
                                       maakt. Bij een klascode die op
                                       werkbladen en op het bord staat, is dat
                                       het gevolg dat telt — en de enige manier
                                       om een code van vorig schooljaar dicht
                                       te zetten, want verlopen doet hij niet
                                       vanzelf.
                                       */}
                                      <button onClick={() => setRegenerateCodeClassroomId(classroom.id)} className="text-xs text-indigo-600 hover:text-indigo-700 font-bold ml-1" title="Vervangt de code; de oude werkt daarna niet meer" data-id="element-10304" data-mp="11tyvfp409w9f">
                              
                                        Vervangen
                                      </button>
                                    </>}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2" data-id="element-10305" data-mp="312h1c9mjjrhm">
                            {newClassroomId === classroom.id && <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-md mr-2" data-id="element-10306" data-mp="2w3dx2redxpap">
                                Nieuw!
                              </span>}
                            {getClassroomRole(accounts, classroom.id, activeAccount.id) === 'owner' ? <>
                                <Button onClick={() => {
                      if (isEditingClass !== classroom.id) {
                        setClassWarnings([]);
                      }
                      setIsEditingClass(classroom.id);
                      setEditClassName(classroom.name);
                      setEditGroupLevel(classroom.groupLevel);
                    }} variant="ghost" size="sm" data-id="element-10307" data-mp="3jt860xvl2tpo">
                        
                                  <Edit2 className="w-4 h-4 mr-2" data-id="element-10308" data-mp="6ln931bt8sw7" /> Bewerk
                                </Button>
                                <Button onClick={() => setTransferClassroomId(classroom.id)} variant="ghost" size="sm" data-id="element-10309" data-mp="18dohmjo68eic">
                        
                                  Draag over...
                                </Button>
                                <Button onClick={() => setDeleteClassroomId(classroom.id)} variant="ghost" size="sm" className="text-rose-600 hover:bg-rose-50 hover:text-rose-700" data-id="element-10310" data-mp="1ccxaznfauadd">
                        
                                  <Trash2 className="w-4 h-4" data-id="element-10311" data-mp="1mcedjvz98854" />
                                </Button>
                              </> : <span className="text-[10px] font-black uppercase tracking-wider px-2 py-1 bg-amber-100 text-amber-700 rounded" title="Je bent co-docent in deze klas — alleen de eigenaar kan klasinstellingen wijzigen" data-id="element-10312" data-mp="201ydyv5d4tb9">
                      
                                Co-docent
                              </span>}
                          </div>
                        </>}
                    </motion.div>)}
                </div>}
            </div>
          </Card>}

        {/* Teachers Management Section */}
        {activeTab === 'docenten' && <div className="space-y-6" data-id="element-10313" data-mp="2tkmsysp208wt">
            {activeClassroom ? <ClassroomTeachersTab classroom={activeClassroom} activeAccount={activeAccount} data-id="element-10314" data-mp="2eox0qct9yzjn" /> : <div className="text-center py-10 bg-slate-50 rounded-xl border border-slate-200 border-dashed" data-id="element-10315" data-mp="2jo07q2stffu9">
                <div className="mb-4 flex justify-center" data-id="element-10316" data-mp="25wmq4uegws1v">
                  <DjayNarrator size="md" emotion="thinking" data-id="element-10317" data-mp="2anv0bsnmghg3" />
                </div>
                <p className="text-slate-500 font-medium" data-id="element-10318" data-mp="2apy2fb21u116">
                  Selecteer eerst een klas in de zijbalk om co-docenten te
                  beheren.
                </p>
              </div>}

            {/* Join as Co-Teacher Section */}
            <Card className="border-slate-200 shadow-sm bg-white overflow-hidden" data-id="element-10319" data-mp="1mzlkr8h04pxe">
              <div className="px-4 py-3 bg-slate-50 border-b border-slate-100" data-id="element-10320" data-mp="1r0nnhza4nk44">
                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-10321" data-mp="3tmj5wngij3z7">
                  Toetreden tot een klas
                </h3>
              </div>
              <div className="p-4" data-id="element-10322" data-mp="3otztvuxh4gwi">
                {!isJoiningAsCoTeacher ? <Button onClick={() => {
              setIsJoiningAsCoTeacher(true);
              setCoTeacherJoinError(null);
            }} variant="secondary" className="w-full sm:w-auto" data-id="element-10323" data-mp="3jy190hbcth0u">
                
                    Ik heb een docentcode
                  </Button> : <div className="p-4 bg-amber-50/60 border border-amber-100 rounded-xl" data-id="element-10324" data-mp="bhfy2uz5wdv3">
                    <h4 className="text-sm font-bold text-slate-900 mb-1" data-id="element-10325" data-mp="6i3a26wk10qx">
                      Ik heb een docentcode
                    </h4>
                    <p className="text-xs text-slate-600 mb-3" data-id="element-10326" data-mp="2fq5hltjn8vq">
                      Vraag de klas-eigenaar om de <strong data-id="element-10327" data-mp="2r9dh42w3nteb">docentcode</strong> van
                      8 tekens. Dat is een andere code dan de klascode die
                      leerlingen gebruiken.
                    </p>
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3" data-id="element-10328" data-mp="12ppuu7ywce9e">
                      <div className="flex-1 w-full" data-id="element-10329" data-mp="16rbfifho9wu5">
                        <Input variant="admin" label="Docentcode (8 tekens)" value={coTeacherCode} onChange={e => setCoTeacherCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))} onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleJoinAsCoTeacher();
                    }
                  }} placeholder="Bijv. K7M2QP4X" maxLength={CO_TEACHER_CODE_LENGTH} autoFocus data-id="element-10330" data-mp="16nxebakqvtoa" />
                    
                        {coTeacherJoinError && <p className="mt-2 text-xs text-rose-600" data-id="element-10331" data-mp="1zeyu9dw9ht8v">
                            {coTeacherJoinError}
                          </p>}
                      </div>
                      <div className="flex items-center gap-2 self-end sm:self-auto mt-2 sm:mt-5" data-id="element-10332" data-mp="3ny6r72zqae">
                        <Button onClick={handleJoinAsCoTeacher} className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl text-sm font-bold transition-all flex items-center gap-2" data-id="element-10333" data-mp="5167wjsxa9jg">
                      
                          Word co-docent
                        </Button>
                        <Button onClick={() => {
                    setIsJoiningAsCoTeacher(false);
                    setCoTeacherCode('');
                    setCoTeacherJoinError(null);
                  }} variant="secondary" data-id="element-10334" data-mp="3ftjrngvr5zmw">
                      
                          <X className="w-4 h-4" data-id="element-10335" data-mp="2i7cevjpap0zg" />
                        </Button>
                      </div>
                    </div>
                  </div>}
              </div>
            </Card>
          </div>}

        {/* Archive Section */}
        {activeTab === 'archief' && recentArchivedLinks.length > 0 && <Card className="border-slate-200 shadow-sm bg-white overflow-hidden" data-id="element-10336" data-mp="24ed2vroq1own">
            <div className="p-4 border-b border-slate-100 bg-slate-50" data-id="element-10337" data-mp="1zgl7uoss5mo8">
              <div className="flex items-center gap-2" data-id="element-10338" data-mp="1ze8r2eyrdicr">
                <ArchiveRestore className="w-5 h-5 text-slate-500" data-id="element-10339" data-mp="ex93hywk1tr3" />
                <h3 className="text-sm font-bold text-slate-900" data-id="element-10340" data-mp="jp9a2a74rkwn">
                  Onlangs ontkoppeld ({recentArchivedLinks.length})
                </h3>
              </div>
              <p className="text-xs text-slate-500 mt-1" data-id="element-10341" data-mp="4y0e7jcmx3b2">
                Na 30 dagen worden ontkoppelingen automatisch definitief.
              </p>
            </div>

            <div className="divide-y divide-slate-100 bg-white" data-id="element-10342" data-mp="xp1yfyv6pp1g">
              {recentArchivedLinks.map(link => {
            const learner = accounts.accountsById[link.learnerId];
            if (!learner) return null;
            const daysAgo = Math.floor((Date.now() - link.archivedAt) / (1000 * 60 * 60 * 24));
            const timeText = daysAgo === 0 ? 'Vandaag' : daysAgo === 1 ? 'Gisteren' : `${daysAgo} dagen geleden`;
            let contextText = 'uit account';
            if (link.reason === 'unlink' && link.fromClassroomIds.length > 0) {
              const classNames = link.fromClassroomIds.map(id => classrooms.find(c => c.id === id)?.name).filter(Boolean);
              if (classNames.length > 0) {
                contextText = `uit ${classNames.join(', ')}`;
              }
            } else if (link.reason === 'classroom_delete') {
              contextText = 'door klasverwijdering';
            }
            return <div key={link.learnerId} className="flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors" data-id="element-10343" data-mp="116g4ki2790a3">
                  
                    <div data-id="element-10344" data-mp="w5s3t4o6552o">
                      <div className="font-bold text-sm text-slate-900" data-id="element-10345" data-mp="w9fvrzdytjfa">
                        {learner.displayName}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5" data-id="element-10346" data-mp="m9yx41x4s5st">
                        {timeText} · {contextText}
                      </div>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-10347" data-mp="2gng8yro6be80">
                      <Button onClick={() => {
                  restoreArchivedLearner(activeAccount.id, link.learnerId, {
                    toClassroomId: link.fromClassroomIds[0]
                  });
                  toast.success(`${learner.displayName} is hersteld.`);
                }} variant="secondary" size="sm" className="text-xs font-bold" data-id="element-10348" data-mp="2gnzqi79ww0li">
                      
                        Herstel
                      </Button>
                    </div>
                  </div>;
          })}
            </div>
          </Card>}
      </div>

      <AddLearnerSheet open={showAddStudent} onClose={() => setShowAddStudent(false)} activeAccount={activeAccount} classrooms={classrooms} selectedClassroomId={selectedClassroomId} onComplete={() => setShowAddStudent(false)} data-id="element-10349" data-mp="2a6epxdfq1q9s" />
      

      <CoTeacherWelcomeModal open={!!coTeacherWelcomeFor} classroom={coTeacherWelcomeFor} onClose={() => setCoTeacherWelcomeFor(null)} data-id="element-10350" data-mp="2k6p5v5t55bsn" />
      

      <TransferClassroomModal open={!!transferClassroomId} classroom={classrooms.find(c => c.id === transferClassroomId) || null} activeAccount={activeAccount} onClose={() => setTransferClassroomId(null)} data-id="element-10351" data-mp="3vkasaj7m8zah" />
      

      <ConfirmDialog open={!!regenerateCodeClassroomId} title="Klascode vervangen" message="De oude code stopt direct met werken. Leerlingen die hem nog niet gebruikt hebben, moeten de nieuwe krijgen. Leerlingen die al in de klas zitten, blijven gewoon staan. Doe dit aan het begin van een schooljaar: een klascode verloopt niet vanzelf, dus een code op een oud werkblad werkt anders nog steeds." confirmLabel="Ja, vervangen" cancelLabel="Annuleren" variant="danger" onConfirm={() => {
      if (regenerateCodeClassroomId) {
        regenerateJoinCode(activeAccount.id, regenerateCodeClassroomId);
        setRegenerateCodeClassroomId(null);
      }
    }} onCancel={() => setRegenerateCodeClassroomId(null)} data-id="element-10352" data-mp="ghvta9g45m7o" />
      

      <ConfirmDialog open={!!deleteClassroomId} title="Klas verwijderen" message={classroomToDelete ? `Weet je zeker dat je "${classroomToDelete.name}" wilt verwijderen?\n\n• ${classroomToDelete.learnerIds.length} leerlingen blijven gekoppeld aan je account.\n• ${classroomToDelete.coTeacherIds?.length || 0} co-docenten verliezen direct toegang.\n• Koppelcode ${classroomToDelete.joinCode} vervalt direct.\n\nJe kunt dit niet ongedaan maken.` : ''} confirmLabel="Ja, verwijder klas" cancelLabel="Annuleren" variant="danger" onConfirm={() => {
      if (deleteClassroomId) {
        removeClassroom(activeAccount.id, deleteClassroomId);
        setDeleteClassroomId(null);
      }
    }} onCancel={() => setDeleteClassroomId(null)} data-id="element-10353" data-mp="3f013r27e23r9" />
      

      <ConfirmDialog open={!!pendingRemoveStudent} title="Leerling verwijderen uit klas" message={pendingRemoveStudent ? `Weet je zeker dat je ${pendingRemoveStudent.studentName} uit ${pendingRemoveStudent.classroomName} wilt verwijderen? ${pendingRemoveStudent.studentName} blijft wel gekoppeld aan jouw account.` : ''} confirmLabel="Ja, verwijder" cancelLabel="Annuleren" variant="danger" onConfirm={() => {
      if (pendingRemoveStudent) {
        removeLearnerFromClassroom(activeAccount.id, pendingRemoveStudent.classroomId, pendingRemoveStudent.studentId);
        setPendingRemoveStudent(null);
      }
    }} onCancel={() => setPendingRemoveStudent(null)} data-id="element-10354" data-mp="1bocxfqof9bki" />
      

      <ConfirmDialog open={!!pendingUnlinkStudent} title="Koppeling verbreken" message={pendingUnlinkStudent ? `${pendingUnlinkStudent.studentName} wordt verwijderd uit al je klassen en je verliest toegang tot de voortgang. Het leerlingaccount blijft bestaan.` : ''} confirmLabel="Ja, ontkoppel" cancelLabel="Annuleren" variant="danger" onConfirm={() => {
      if (pendingUnlinkStudent) {
        const studentId = pendingUnlinkStudent.studentId;
        const studentName = pendingUnlinkStudent.studentName;
        unlinkTeacherFromLearner(activeAccount.id, studentId);
        setPendingUnlinkStudent(null);
        toast.success(`${studentName} is ontkoppeld`, {
          action: {
            label: 'Ongedaan maken',
            onClick: () => {
              restoreArchivedLearner(activeAccount.id, studentId);
              toast.success(`${studentName} is weer gekoppeld.`);
            }
          },
          duration: 5000
        });
      }
    }} onCancel={() => setPendingUnlinkStudent(null)} data-id="element-10355" data-mp="1wula4iz0f5at" />
      
    </PageLayout>;
}